import { useEffect, useState, useCallback } from "react";

type Theme = "light" | "dark" | "system";
const KEY = "lz_crm_theme";

const apply = (theme: Theme) => {
  const root = document.documentElement;
  const isDark =
    theme === "dark" ||
    (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  root.classList.toggle("dark", isDark);
  root.style.colorScheme = isDark ? "dark" : "light";
};

export function useDarkMode() {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return "system";
    return ((localStorage.getItem(KEY) as Theme | null) ?? "system");
  });

  useEffect(() => {
    apply(theme);
    if (theme === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const onChange = () => apply("system");
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    localStorage.setItem(KEY, t);
    setThemeState(t);
  }, []);

  const toggle = useCallback(() => {
    setTheme(
      theme === "dark" ? "light" : theme === "light" ? "system" : "dark",
    );
  }, [theme, setTheme]);

  return { theme, setTheme, toggle };
}

/** Eagerly apply theme before React hydrates so there's no flash of light mode. */
export function bootTheme() {
  if (typeof window === "undefined") return;
  const stored = (localStorage.getItem(KEY) as Theme | null) ?? "system";
  apply(stored);
}
